import * as React from "react";
import { cn } from "@/lib/utils";

// P0-C.3: reusable PHP peso input. Presentation-only ₱ prefix + thousands
// separators while typing/pasting; emits a RAW numeric string via onValueChange
// so parent state and API payloads stay numeric (e.g. "7500000").
// `decimal` (opt-in) keeps integer callers byte-identical while allowing up to
// `maxDecimals` fractional digits (e.g. Monthly Downpayment "23,216.67" -> "23216.67").
const toDigits = (s) => String(s ?? "").replace(/\D/g, "");
const formatDigits = (raw) => {
  const digits = toDigits(raw);
  return digits ? Number(digits).toLocaleString("en-PH") : "";
};

// Decimal mode: normalize free text to a raw numeric string (digits + single dot, clamped decimals).
const toDecimalRaw = (s, maxDecimals) => {
  let v = String(s ?? "").replace(/[^\d.]/g, "");
  const first = v.indexOf(".");
  if (first !== -1) {
    v = v.slice(0, first + 1) + v.slice(first + 1).replace(/\./g, "");
    const [intp, decp = ""] = v.split(".");
    v = intp + "." + decp.slice(0, maxDecimals);
  }
  return v;
};
// Decimal mode: group the integer part with separators, preserve the (in-progress) fractional part.
const formatDecimal = (raw) => {
  const s = String(raw ?? "");
  if (s === "") return "";
  const hasDot = s.includes(".");
  const [intPart, decPart = ""] = s.split(".");
  const intDigits = intPart.replace(/\D/g, "");
  const fInt = intDigits ? Number(intDigits).toLocaleString("en-PH") : "0";
  return hasDot ? `${fInt}.${decPart}` : fInt;
};

const CurrencyInput = React.forwardRef(
  ({ className, value, onValueChange, disabled, placeholder, decimal = false, maxDecimals = 2, ...props }, ref) => {
    const innerRef = React.useRef(null);
    const caretDigits = React.useRef(null); // digit-offset to restore after reformat
    React.useImperativeHandle(ref, () => innerRef.current);

    const display = decimal ? formatDecimal(value) : formatDigits(value);

    // Restore caret by digit offset so editing mid-number doesn't jump to the end.
    React.useLayoutEffect(() => {
      const el = innerRef.current;
      if (!el || caretDigits.current == null) return;
      const target = caretDigits.current;
      caretDigits.current = null;
      let seen = 0;
      let pos = target === 0 ? 0 : display.length;
      if (target !== 0) {
        for (let i = 0; i < display.length; i++) {
          if (/\d/.test(display[i])) {
            seen += 1;
            if (seen === target) { pos = i + 1; break; }
          }
        }
      }
      el.setSelectionRange(pos, pos);
    });

    const handleChange = (e) => {
      const el = e.target;
      const sel = el.selectionStart || 0;
      caretDigits.current = (el.value.slice(0, sel).match(/\d/g) || []).length;
      onValueChange?.(decimal ? toDecimalRaw(el.value, maxDecimals) : toDigits(el.value));
    };

    return (
      <div className={cn("relative w-full", className)}>
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm select-none">₱</span>
        <input
          ref={innerRef}
          type="text"
          inputMode={decimal ? "decimal" : "numeric"}
          autoComplete="off"
          disabled={disabled}
          value={display}
          placeholder={placeholder}
          onChange={handleChange}
          className="flex h-11 w-full rounded-md border border-input bg-transparent pl-8 pr-3 py-1 text-base shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
          {...props}
        />
      </div>
    );
  }
);
CurrencyInput.displayName = "CurrencyInput";

export { CurrencyInput };
